import React from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ChatMessageToastProps {
  open: boolean;
  fromDisplayName?: string | null;
  fromAvatarUrl?: string | null;
  roomTitle?: string | null;
  message: string;
  imageUrl?: string | null;
  createdAt?: string | null;
  onClick: () => void;
  onClose: () => void;
  offset?: number; // dodatkowe przesunięcie od góry w px, do układania stosu
}

function timeAgo(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const now = Date.now();
    const t = new Date(iso).getTime();
    const diffSec = Math.max(1, Math.floor((now - t) / 1000));
    if (diffSec < 60) return `${diffSec} s temu`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min temu`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} godzin${diffH===1?'ę':'y'} temu`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return 'wczoraj';
    if (diffD === 2) return 'przedwczoraj';
    if (diffD < 7) return `${diffD} dni temu`;
    const diffW = Math.floor(diffD / 7);
    if (diffW === 1) return 'tydzień temu';
    return `${diffW} tyg. temu`;
  } catch {
    return null;
  }
}

const ChatMessageToast: React.FC<ChatMessageToastProps> = ({ open, fromDisplayName, fromAvatarUrl, roomTitle, message, imageUrl, createdAt, onClick, onClose, offset = 0 }) => {
  if (!open) return null;
  const isImage = Boolean(imageUrl);
  const isPrivate = (roomTitle || '').toLowerCase().includes('prywat');
  const HEIGHT_TEXT = 88;
  const HEIGHT_IMAGE = 160; // stała wysokość dymka dla obrazu (miniatura + nagłówki)
  const ago = timeAgo(createdAt);
  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: '64px', sm: '72px' },
        right: 16,
        zIndex: 2100,
        width: { xs: 320, sm: 360 },
        bgcolor: '#3F3FDC',
        color: '#FFFFFF',
        borderRadius: '4px',
        boxShadow: '0 10px 26px rgba(63, 63, 220, 0.25)',
        border: '1px solid rgba(255,255,255,0.25)',
        transform: `translateY(${offset}px)`,
        transition: 'transform 180ms ease',
        willChange: 'transform',
        height: isImage ? HEIGHT_IMAGE : HEIGHT_TEXT,
        overflow: 'hidden',
        fontFamily: 'Roboto, sans-serif'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25 }}>
        <Avatar src={fromAvatarUrl || undefined} sx={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.6)' }}>
          {(fromDisplayName || '?').slice(0,1).toUpperCase()}
        </Avatar>
        <Box onClick={onClick} sx={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
          {isPrivate ? (
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              WIADOMOŚĆ PRYWATNA
            </Typography>
          ) : roomTitle ? (
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {roomTitle}
            </Typography>
          ) : null}
          {fromDisplayName && (
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#FFFFFF' }} noWrap>
              {fromDisplayName}
            </Typography>
          )}
          {isImage ? (
            <Box sx={{ mt: 0.5 }}>
              <Box component="img" src={imageUrl || undefined} alt="Załącznik" sx={{ display: 'block', width: 200, height: 104, objectFit: 'cover', borderRadius: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.18)', backgroundColor: 'rgba(255,255,255,0.2)' }} />
              {!!message && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#FFFFFF' }} noWrap>
                  {message}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: '#FFFFFF' }}>
              {message}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 0.5 }}>
          <IconButton size="small" onClick={onClose} aria-label="Zamknij" sx={{ color: '#FFFFFF' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      {ago && (
        <Typography variant="caption" sx={{ position: 'absolute', right: 12, bottom: 4, color: '#FFFFFF', opacity: 0.85, fontSize: 10 }}>
          {ago}
        </Typography>
      )}
    </Box>
  );
};

export default ChatMessageToast;


